import './components/login/login.component.js';
import './components/template-picker/template-picker.component.js';
import './components/page-form/page-form.component.js';
import './components/page-list/page-list.component.js';
import './components/notification/notification.component.js';
import './components/empty-state/empty-state.component.js';
import './components/confirm-dialog/confirm-dialog.component.js';
import './components/cold-start-splash/cold-start-splash.component.js';
import './components/site-preview/site-preview.component.js';

import {AuthApiAdapter} from './shell/adapters/driven/auth.api-adapter.js';
import {PageApiAdapter} from './shell/adapters/driven/page.api-adapter.js';
import {AuthEventAdapter} from './shell/adapters/driving/auth.event-adapter.js';
import {PageEventAdapter} from './shell/adapters/driving/page.event-adapter.js';
import {SitePreviewEventAdapter} from './shell/adapters/driving/site-preview.event-adapter.js';
import {RouteGuard} from '@web-builder/shared/route-guard.js';
import {t, getLocale, setLocale, applyTranslations, applyDocumentLocale} from './i18n/i18n.js';

/** Translates the light DOM, tells assistive technology which language the page is in and titles the tab. */
function applyGlobalTranslations() {
    applyTranslations(document);
    applyDocumentLocale();
}

/** Marks the header language button of the current locale, visibly and as pressed. */
function syncLocaleButtons() {
    const current = getLocale();
    document.querySelectorAll('.btn-locale').forEach((btn) => {
        btn.classList.toggle('active', btn.dataset.locale === current);
        btn.setAttribute('aria-pressed', String(btn.dataset.locale === current));
    });
}

/** Header language buttons; a change from anywhere (such as the login screen) updates them. */
function initLocaleSwitcher() {
    syncLocaleButtons();
    document.querySelectorAll('.btn-locale').forEach((btn) => {
        btn.addEventListener('click', () => setLocale(btn.dataset.locale));
    });
    document.addEventListener('locale-change', applyGlobalTranslations);
    document.addEventListener('locale-change', syncLocaleButtons);
}

/**
 * Keeps a file dragged onto the app from replacing it: without a handler the browser opens a
 * dropped file in place of the page, losing the form. Drop zones that accept files cancel the
 * event themselves first. Over any other spot the drop effect is `none`, so the cursor shows
 * that a drop does nothing there.
 * @param {DragEvent} event
 */
function preventFileDrop(event) {
    if (!event.defaultPrevented && [...(event.dataTransfer?.types ?? [])].includes('Files')) {
        event.preventDefault();
        if (event.type === 'dragover') {
            event.dataTransfer.dropEffect = 'none';
        }
    }
}

const AUTH_BASE = window.__AUTH_BASE__ || '/api/auth';
const CLIENTS_BASE = window.__CLIENTS_BASE__ || '/api/client';

export function bootstrap() {
    applyGlobalTranslations();
    initLocaleSwitcher();
    const loginEl = document.querySelector('user-login');
    const appEl = document.querySelector('#app');
    const pickerEl = document.querySelector('template-picker');
    const formEl = document.querySelector('page-form');
    const listEl = document.querySelector('page-list');
    const notificationEl = document.querySelector('user-notification');
    const coldStartSplashEl = document.querySelector('cold-start-splash');

    let pageAdapter;
    let sitePreviewAdapter;
    let currentAuth = null;
    let refreshInterval = null;
    let userToggleClickHandler = null;
    let documentDropdownClickHandler = null;
    let logoutClickHandler = null;
    let dropdownKeydownHandler = null;
    let destroyed = false;
    /**
     * The route on screen when the session expired and whose session it was; the next sign-in of
     * the same account returns to it.
     * @type {{hash: string, email: string} | null}
     */
    let resumeRoute = null;
    /** Leaving a form with unsaved changes asks first; staying keeps the form's route. */
    const routeGuard = new RouteGuard({
        mayLeave: async () => !formHasUnsavedChanges() || confirmLeave(),
        show: showRoute,
    });

    async function refreshSession() {
        if (!currentAuth) return;
        try {
            const freshAuth = await authApiAdapter.refresh(currentAuth.token);
            currentAuth = freshAuth;
            // Update the adapter with the new token
            const pageApiAdapter = new PageApiAdapter(CLIENTS_BASE, freshAuth.token);
            pageAdapter.updateApiAdapter(pageApiAdapter);
            sitePreviewAdapter.updateApiAdapter(pageApiAdapter);
            console.debug('Session refreshed successfully');
        } catch (e) {
            console.warn('Session refresh failed', e);
            if (e.status === 401) {
                signOutExpiredSession();
                notificationEl.show(t('global.sessionExpired'), 'error');
            }
        }
    }

    async function onAuthenticated(auth) {
        if (currentAuth) {
            deactivate();
        }
        currentAuth = auth;
        loginEl.style.display = 'none';
        appEl.classList.remove('hidden');

        document.querySelector('#user-email').textContent = auth.email;
        document.querySelector('#client-name').textContent = auth.clientName;
        const initial = (auth.clientName || '').trim().charAt(0).toUpperCase();
        if (initial) document.querySelector('#user-initial').textContent = initial;
        pickerEl.clientName = auth.clientName;
        formEl.domain = auth.domain;
        listEl.domain = auth.domain;

        const userToggle = document.querySelector('#user-toggle');
        const userDropdown = document.querySelector('#user-dropdown');

        const setDropdownOpen = (open) => {
            userDropdown.classList.toggle('hidden', !open);
            userToggle.setAttribute('aria-expanded', String(open));
        };
        userToggleClickHandler = (e) => {
            e.stopPropagation();
            setDropdownOpen(userDropdown.classList.contains('hidden'));
        };
        documentDropdownClickHandler = () => setDropdownOpen(false);
        dropdownKeydownHandler = (e) => {
            if (e.key !== 'Escape' || userDropdown.classList.contains('hidden')) return;
            // Handled here, so the same Escape does not also close a toast.
            e.preventDefault();
            setDropdownOpen(false);
            userToggle.focus();
        };
        logoutClickHandler = async () => {
            if (formHasUnsavedChanges() && !(await confirmLeave())) return;
            deactivate();
        };

        userToggle.addEventListener('click', userToggleClickHandler);
        document.addEventListener('click', documentDropdownClickHandler);
        document.addEventListener('keydown', dropdownKeydownHandler);

        const pageApiAdapter = new PageApiAdapter(CLIENTS_BASE, auth.token);
        pageAdapter = new PageEventAdapter(pageApiAdapter, pickerEl, formEl, listEl, notificationEl, signOutExpiredSession);
        pageAdapter.connect();
        sitePreviewAdapter = new SitePreviewEventAdapter(pageApiAdapter, notificationEl, signOutExpiredSession);
        sitePreviewAdapter.connect();

        // Refresh every 30 minutes
        refreshInterval = setInterval(refreshSession, 30 * 60 * 1000);

        document.querySelector('#btn-logout').addEventListener('click', logoutClickHandler);
        if (resumeRoute?.email === auth.email) {
            // Set before the hashchange listener is added: the route guard accepts it below, so
            // the hashchange this causes names the route on screen and needs nothing.
            location.hash = resumeRoute.hash;
        }
        resumeRoute = null;
        window.addEventListener('hashchange', routeGuard.handleHashChange);
        window.addEventListener('beforeunload', onBeforeUnload);
        await routeGuard.accept();
    }

    /**
     * Signs out in place once a request finds the session expired. Reloading the app instead would
     * make the browser ask to confirm leaving an unsaved form, and staying would keep an app whose
     * every request fails; the form's changes cannot be saved without a session either way. The
     * next sign-in returns to the route that was on screen.
     */
    function signOutExpiredSession() {
        if (currentAuth) {
            resumeRoute = {hash: location.hash || '#/', email: currentAuth.email};
            deactivate();
        }
    }

    /** Whether the page form is on screen with changes that are not saved yet. */
    function formHasUnsavedChanges() {
        return formEl.style.display !== 'none' && formEl.hasUnsavedChanges === true;
    }

    /** Asks whether to leave the form and lose its unsaved changes. */
    function confirmLeave() {
        return document.querySelector('user-confirm-dialog').ask({
            title: t('pageForm.confirmLeaveTitle'),
            body: t('pageForm.confirmLeaveBody'),
            confirmLabel: t('pageForm.confirmLeaveAction'),
            cancelLabel: t('pageForm.stayAction'),
            danger: true,
        });
    }

    /** Closing or reloading the tab asks the browser to confirm while the form holds unsaved changes. */
    function onBeforeUnload(event) {
        if (!formHasUnsavedChanges()) return;
        event.preventDefault();
        event.returnValue = '';
    }

    function deactivate() {
        if (refreshInterval) {
            clearInterval(refreshInterval);
            refreshInterval = null;
        }
        currentAuth = null;
        if (pageAdapter) {
            pageAdapter.disconnect();
            pageAdapter = null;
        }
        if (sitePreviewAdapter) {
            sitePreviewAdapter.disconnect();
            sitePreviewAdapter = null;
        }
        const userToggle = document.querySelector('#user-toggle');
        if (userToggle && userToggleClickHandler) {
            userToggle.removeEventListener('click', userToggleClickHandler);
        }
        if (documentDropdownClickHandler) {
            document.removeEventListener('click', documentDropdownClickHandler);
        }
        if (dropdownKeydownHandler) {
            document.removeEventListener('keydown', dropdownKeydownHandler);
        }
        document.querySelector('#user-dropdown')?.classList.add('hidden');
        document.querySelector('#user-toggle')?.setAttribute('aria-expanded', 'false');
        const logoutButton = document.querySelector('#btn-logout');
        if (logoutButton && logoutClickHandler) {
            logoutButton.removeEventListener('click', logoutClickHandler);
        }
        userToggleClickHandler = null;
        documentDropdownClickHandler = null;
        dropdownKeydownHandler = null;
        logoutClickHandler = null;
        window.removeEventListener('hashchange', routeGuard.handleHashChange);
        window.removeEventListener('beforeunload', onBeforeUnload);
        routeGuard.reset();
        hideAll();
        appEl.classList.add('hidden');
        loginEl.style.display = '';
        location.hash = '#/';
    }

    function hideAll() {
        pickerEl.style.display = 'none';
        formEl.style.display = 'none';
        listEl.style.display = 'none';
    }

    /** @param {'home'|'pages'} current */
    function markCurrentNav(current) {
        document.querySelectorAll('[data-nav]').forEach((link) => {
            if (link.dataset.nav === current) {
                link.setAttribute('aria-current', 'page');
            } else {
                link.removeAttribute('aria-current');
            }
        });
    }

    /** @param {string} hash an accepted route */
    async function showRoute(hash) {
        const publishMatch = hash.match(/^#\/publish\/([^?]+)/);
        const editMatch = hash.match(/^#\/edit\/([^?]+)/);

        hideAll();
        markCurrentNav(editMatch || hash === '#/pages' ? 'pages' : 'home');

        if (publishMatch) {
            formEl.style.display = '';
            formEl.editMode = false;
            const templateName = decodeURIComponent(publishMatch[1]);
            formEl.templateName = templateName;
            await pageAdapter.loadTemplateDetail(templateName);
        } else if (editMatch) {
            formEl.style.display = '';
            const templateName = decodeURIComponent(editMatch[1]);
            const params = new URLSearchParams(hash.split('?')[1] || '');
            const pagePath = params.get('path');
            formEl.templateName = templateName;
            await pageAdapter.loadEditPage(templateName, pagePath, {live: params.get('draft') !== '1', pending: params.get('pending') === '1'});
        } else if (hash === '#/pages') {
            listEl.style.display = '';
            await pageAdapter.loadPages();
        } else {
            pickerEl.style.display = '';
            await pageAdapter.loadTemplates();
        }
    }

    // The cold-start delay (~1.2s) happens during the auth API call itself,
    // so the splash must trigger on submit and dismiss on auth-request-complete.
    function startColdStartSplash() {
        if (!coldStartSplashEl?.runWhile) return;
        coldStartSplashEl.runWhile(new Promise((resolve) => {
            document.addEventListener('auth-request-complete', resolve, {once: true});
        }));
    }
    document.addEventListener('auth-login', startColdStartSplash);
    document.addEventListener('auth-register', startColdStartSplash);

    window.addEventListener('dragover', preventFileDrop);
    window.addEventListener('drop', preventFileDrop);

    const authApiAdapter = new AuthApiAdapter(AUTH_BASE);
    const authEventAdapter = new AuthEventAdapter(authApiAdapter, notificationEl, onAuthenticated);
    authEventAdapter.connect();

    return {
        destroy() {
            if (destroyed) {
                return;
            }
            destroyed = true;
            authEventAdapter.disconnect();
            deactivate();
            document.removeEventListener('locale-change', applyGlobalTranslations);
            document.removeEventListener('locale-change', syncLocaleButtons);
            window.removeEventListener('dragover', preventFileDrop);
            window.removeEventListener('drop', preventFileDrop);
        }
    };
}

if (!window.__WB_DISABLE_AUTO_BOOTSTRAP__) {
    bootstrap();
}
